import { useState, useEffect } from 'react';
import { trendsApi } from '../lib/api';
import TrendCard from '../components/trends/TrendCard';
import TrendMatchCard from '../components/trends/TrendMatchCard';

const TABS = [
  { key: 'matches', label: 'Your Matches' },
  { key: 'all', label: 'All Trends' },
];

const MATCH_FILTERS = [
  { value: '', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'saved', label: 'Saved' },
  { value: 'acted_on', label: 'Applied' },
  { value: 'dismissed', label: 'Dismissed' },
];

export default function TrendIntelligence({ onApplyToPitch }) {
  const [tab, setTab] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [trends, setTrends] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [matchFilter, setMatchFilter] = useState('');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, matchFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (tab === 'all') {
        const data = await trendsApi.list();
        setTrends(data.trends || []);
      } else {
        const params = {};
        if (matchFilter) params.status = matchFilter;
        const data = await trendsApi.getMatches(params);
        setMatches(data.matches || []);
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleScan = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      const data = await trendsApi.scan();
      setScanResult(data);
      // Reload matches
      const matchData = await trendsApi.getMatches(matchFilter ? { status: matchFilter } : {});
      setMatches(matchData.matches || []);
      setTab('matches');
    } catch (err) {
      console.error(err);
    }
    setScanning(false);
  };

  const handleUpdateStatus = async (matchId, status) => {
    try {
      await trendsApi.updateMatchStatus(matchId, status);
      await loadData();
    } catch (err) {
      console.error(err);
    }
  };

  const newCount = matches.filter(m => m.status === 'new').length;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl text-vault-navy">Current Trends</h1>
          <p className="text-gray-400 text-sm mt-1">
            Social and cultural trends matched against your ideas
          </p>
        </div>
        <button
          onClick={handleScan}
          disabled={scanning}
          className="px-4 py-2.5 bg-vault-navy hover:bg-vault-slate text-white font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {scanning ? (
            <>
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Scanning...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Scan Trends Against Vault
            </>
          )}
        </button>
      </div>

      {/* Scan result banner */}
      {scanResult && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl">
          <p className="text-sm text-green-700">
            Scanned {scanResult.scanned?.ideas || 0} ideas against {scanResult.scanned?.trends || 0} trends.
            Found <strong>{scanResult.matches?.length || 0}</strong> new match{scanResult.matches?.length !== 1 ? 'es' : ''}.
          </p>
          <button onClick={() => setScanResult(null)} className="text-xs text-green-500 mt-1 hover:underline">Dismiss</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-vault-surface rounded-lg p-1 w-fit">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === t.key
                ? 'bg-white text-vault-navy shadow-sm'
                : 'text-gray-500 hover:text-vault-navy'
            }`}
          >
            {t.label}
            {t.key === 'matches' && newCount > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 bg-vault-gold text-white text-xs rounded-full">
                {newCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-16 text-gray-400">Loading...</div>
      ) : tab === 'all' ? (
        /* All Trends view */
        trends.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-5xl mb-4 opacity-30">&#128200;</div>
            <h3 className="text-lg font-semibold text-vault-navy mb-2">No trends yet</h3>
            <p className="text-gray-500 text-sm">Trends are detected via scheduled monitoring. Check back soon.</p>
          </div>
        ) : (
          <>
            {/* Category filter */}
            <div className="flex gap-1.5 flex-wrap mb-5">
              <button
                onClick={() => setCategoryFilter('')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  !categoryFilter ? 'bg-vault-gold text-white' : 'bg-white border border-vault-border text-gray-600 hover:border-vault-gold/50'
                }`}
              >All</button>
              {[...new Set(trends.map(t => t.trend_category).filter(Boolean))].sort().map(cat => (
                <button
                  key={cat}
                  onClick={() => setCategoryFilter(cat)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors capitalize ${
                    categoryFilter === cat ? 'bg-vault-gold text-white' : 'bg-white border border-vault-border text-gray-600 hover:border-vault-gold/50'
                  }`}
                >{cat.replace(/_/g, ' ')}</button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {trends
                .filter(t => !categoryFilter || t.trend_category === categoryFilter)
                .map(trend => (
                  <TrendCard key={trend.id} trend={trend} />
                ))}
            </div>
          </>
        )
      ) : (
        /* Matches view */
        <>
          {/* Match filters */}
          <div className="flex gap-1.5 flex-wrap mb-4">
            {MATCH_FILTERS.map(f => (
              <button
                key={f.value}
                onClick={() => setMatchFilter(f.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  matchFilter === f.value
                    ? 'bg-vault-gold text-white'
                    : 'bg-white border border-vault-border text-gray-600 hover:border-vault-gold/50'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {matches.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-5xl mb-4 opacity-30">&#127919;</div>
              <h3 className="text-lg font-semibold text-vault-navy mb-2">No trend matches yet</h3>
              <p className="text-gray-500 text-sm mb-4">
                Click "Scan Trends Against Vault" to find trends that match your ideas
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {matches.map(match => (
                <TrendMatchCard
                  key={match.id}
                  match={match}
                  onUpdateStatus={handleUpdateStatus}
                  onApplyToPitch={(m) => {
                    onApplyToPitch?.(m.idea_id, m.trend_id);
                  }}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
